import { create } from 'zustand';
import { User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/services/LoggingService';

interface ProfileState {
  // Loading states
  isCheckingProfile: boolean;
  isCreatingProfile: boolean;
  isCheckingWaitlist: boolean;
  
  // Error state
  profileError: string | null;
  
  // Actions
  checkExistingUser: (email: string) => Promise<boolean | null>;
  createUserProfile: (user: User) => Promise<void>;
  handleWaitlist: (user: User) => Promise<boolean>;
  checkSignupPermissions: () => Promise<boolean>;
  
  // Profile management for authenticated users
  handleUserProfileSetup: (user: User) => Promise<boolean>;
  
  // Utilities
  clearProfileError: () => void;
  setProfileError: (error: string | null) => void;
}

export const useProfileStore = create<ProfileState>((set, get) => ({
  // Initial state
  isCheckingProfile: false,
  isCreatingProfile: false,
  isCheckingWaitlist: false,
  profileError: null,

  // Check if user exists in profiles table (safe with error handling)
  checkExistingUser: async (email: string): Promise<boolean | null> => {
    try {
      const { data: existingUser, error } = await supabase.rpc('user_exists_in_profiles', {
        user_email: email
      });
      
      if (error) {
        // If RLS blocks access, assume user exists to prevent re-creation attempts
        if (error.code === 'PGRST116' || error.message.includes('permission')) {
          logger.warn('ProfileStore', 'Profile check blocked by RLS - assuming existing user', { 
            email,
            error: error.message 
          });
          return true;
        }
        
        logger.error('ProfileStore', 'Error checking existing user', { 
          email,
          error: error.message 
        });
        return null;
      }
      
      return !!existingUser;
    } catch (error) {
      logger.error('ProfileStore', 'Exception checking existing user', { 
        email,
        error 
      });
      return null;
    }
  },

  // Create user profile
  createUserProfile: async (userToCreate: User): Promise<void> => {
    set({ isCreatingProfile: true });
    
    try {
      logger.info('ProfileStore', 'Creating user profile', { userId: userToCreate.id });
      
      // Extract name and avatar data from user metadata
      let firstName = '';
      let lastName = '';
      let avatarUrl = null;
      // Default currency will be set by onboarding; keep null to respect explicit user choices

      // Handle Google auth full_name or individual first/last names
      if (userToCreate.user_metadata?.full_name) {
        const nameParts = userToCreate.user_metadata.full_name.split(' ');
        firstName = nameParts[0] || '';
        lastName = nameParts.slice(1).join(' ') || '';
      } else {
        firstName = userToCreate.user_metadata?.first_name || '';
        lastName = userToCreate.user_metadata?.last_name || '';
      }

      // Get avatar URL from Google auth metadata
      avatarUrl = userToCreate.user_metadata?.avatar_url || userToCreate.user_metadata?.picture || null;

      const { error } = await supabase
        .from('user_profiles')
        .insert({
          id: userToCreate.id,
          email: userToCreate.email,
          first_name: firstName,
          last_name: lastName,
          avatar_url: avatarUrl,
          is_active: true,
          role: 'investor',
          // default_currency will be set during onboarding; omit here to avoid persisting null
        });

      if (error) {
        logger.error('ProfileStore', 'Error creating profile', { error: error.message });
        throw error;
      }

      logger.info('ProfileStore', 'Profile created successfully');
    } catch (error) {
      logger.error('ProfileStore', 'Error in profile creation', { error });
      set({ profileError: error instanceof Error ? error.message : 'Failed to create profile' });
      throw error;
    } finally {
      set({ isCreatingProfile: false });
    }
  },

  // Check if signups are enabled
  checkSignupPermissions: async (): Promise<boolean> => {
    try {
      const { data, error } = await supabase
        .from('system_settings')
        .select('signup_enabled')
        .single();
      
      if (error) {
        logger.error('ProfileStore', 'Error checking signup settings', { error: error.message });
        return true; // Default to enabled if we can't check
      }
      
      return data?.signup_enabled ?? true;
    } catch (error) {
      logger.error('ProfileStore', 'Error in signup check', { error });
      return true;
    }
  },

  // Handle adding user to waitlist
  handleWaitlist: async (userToAdd: User): Promise<boolean> => {
    set({ isCheckingWaitlist: true });
    
    try {
      logger.info('ProfileStore', 'Adding user to waitlist', { email: userToAdd.email });
      
      const { error } = await supabase.rpc('add_to_waitlist', {
        user_email: userToAdd.email || '',
        provider_type_param: 'email',
        provider_id_param: userToAdd.id,
        first_name_param: userToAdd.user_metadata?.first_name || null,
        last_name_param: userToAdd.user_metadata?.last_name || null
      });
      
      if (error) {
        logger.error('ProfileStore', 'Error adding to waitlist', { error: error.message });
        set({ profileError: 'Failed to add to waitlist' });
        return false;
      }
      
      logger.info('ProfileStore', 'Successfully added to waitlist');
      return true;
    } catch (error) {
      logger.error('ProfileStore', 'Error in waitlist handling', { error });
      set({ profileError: error instanceof Error ? error.message : 'Waitlist error' });
      return false;
    } finally {
      set({ isCheckingWaitlist: false });
    }
  },

  // Complete profile setup for authenticated user
  handleUserProfileSetup: async (user: User): Promise<boolean> => {
    if (!user?.email) {
      set({ profileError: 'No email found for user' });
      return false;
    }

    set({ isCheckingProfile: true, profileError: null });

    try {
      // Check if user exists in profiles
      const userExists = await get().checkExistingUser(user.email);
      
      if (userExists === true) {
        // This is an existing user with a profile - always allow
        logger.info('ProfileStore', 'Existing user signed in', { email: user.email });
        
        // User exists, check if they were on waitlist and remove them
        try {
          await supabase
            .from('user_waitlist')
            .delete()
            .eq('email', user.email);
        } catch (waitlistError) {
          // Non-critical error, just log it
          logger.warn('ProfileStore', 'Error removing from waitlist', { error: waitlistError });
        }
        
        return true;
      } else if (userExists === null) {
        // Error occurred during check - be conservative and assume existing user
        logger.warn('ProfileStore', 'Profile check failed - assuming existing user to prevent corruption', { 
          email: user.email 
        });
        return true;
      } else {
        // User does not exist - check signup permissions
        logger.info('ProfileStore', 'User not found in profiles, checking signup permissions');
        
        const signupEnabled = await get().checkSignupPermissions();
        
        if (signupEnabled) {
          logger.info('ProfileStore', 'Signups enabled, creating profile');
          await get().createUserProfile(user);
          return true;
        } else {
          logger.info('ProfileStore', 'Signups disabled, adding to waitlist');
          const waitlistSuccess = await get().handleWaitlist(user);
          
          if (!waitlistSuccess) {
            throw new Error('Failed to add user to waitlist');
          }
          
          // Return false to indicate user should be signed out (waitlist flow)
          return false;
        }
      }
    } catch (error) {
      logger.error('ProfileStore', 'Profile setup error', { error });
      set({ profileError: error instanceof Error ? error.message : 'Profile setup failed' });
      return false;
    } finally {
      set({ isCheckingProfile: false });
    }
  },

  // Clear profile error
  clearProfileError: () => {
    set({ profileError: null });
  },

  // Set profile error
  setProfileError: (error: string | null) => {
    set({ profileError: error });
  }
}));
